import type { SignupFirstTouch, SignupReferralSummary } from './useSignups'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'
import { SIGNUPS_QUERY_KEY } from './useSignups'
import { SIGNUP_ALLOWLIST_QUERY_KEY } from './useSignupAllowlist'
import { SITE_GATE_INVITES_QUERY_KEY } from './useSiteGateInvites'

/**
 * People (/internal/access) — one row per person across the waitlist, the
 * allowlist, pricing access and site-gate invites. API:
 * apps/api/src/features/people-access. Nothing here emails anyone unless the
 * caller sets `send_email: true`.
 */
export type AccessStatus = 'none' | 'requested' | 'invited' | 'granted' | 'expired' | 'revoked'
export type PeopleFilter = 'all' | 'requests' | 'invited' | 'active' | 'revoked'

export type Person = {
  key: string
  email: string | null
  name: string | null
  /** Typed on the waitlist / ROI / request-access form. */
  company: string | null
  /** The platform company their account belongs to. Wins over `company` for display. */
  platform_company: { id: string; name: string } | null
  /** Their demo (Demos page records; demos are per company). */
  demo: {
    company_id: string
    company_name: string
    status: 'purged' | 'converted' | 'never_opened' | 'opened'
    match: 'contact' | 'member' | 'domain'
  } | null
  sources: string[]
  /** Sent an ROI model. ROI senders sort right after referred requests (API-side). */
  roi_submitted: boolean
  /**
   * ONLY the request-access form's five consent keys (newsletterOptIn, newsletterDefault,
   * country, privacyNoticeVersion, privacyNoticeShownAt), as sent, or null when the row has
   * none. Read through `pages/internal/SignupsPage/consentFields.ts`.
   */
  waitlist_consent: { [key: string]: unknown } | null
  /**
   * VRG-728 — where their website visit came from (UTMs, referrer host, landing page), the
   * same shape the signups API returns. Null without one; absent from an older API.
   */
  first_touch?: SignupFirstTouch | null
  website: { status: AccessStatus; invite_id: string | null; expires_at: string | null; use_count: number }
  pricing: { status: AccessStatus; signup_id: string | null }
  platform: { status: AccessStatus; has_account: boolean }
  requested_at: string | null
  last_seen_at: string | null
  note: string | null
  needs_action: boolean
  /**
   * VRG-658 — "Referred by": a brand's referral (company + sender) or an Insider's, or null.
   * Referred REQUESTS are sorted first by the API. Absent if the referral read failed.
   */
  referral?: SignupReferralSummary | null
}

/** What the "shown once" dialog receives. */
export type GrantResult = {
  key: string
  website_url?: string
  pricing_url?: string
  platform_url?: string
  emails_sent: Array<'pricing' | 'platform'>
}

export type InvitePersonPayload = {
  name: string
  email?: string
  link_only?: boolean
  website: { enabled: boolean; expires_at?: string }
  pricing: { enabled: boolean; send_email?: boolean }
  platform: { enabled: boolean; send_email?: boolean }
  note?: string
}

/** Mirrors the API's `matchesFilter` (people-view.ts), which is the tested one. */
export const matchesPeopleFilter = (row: Person, filter: PeopleFilter): boolean => {
  const all = [row.website.status, row.pricing.status, row.platform.status]
  switch (filter) {
    case 'all':
      return true
    case 'requests':
      return row.needs_action
    case 'invited':
      return all.includes('invited')
    case 'active':
      return all.includes('granted')
    case 'revoked':
      return all.some((s) => s === 'revoked' || s === 'expired')
  }
}

export const PEOPLE_QUERY_KEY = 'internal-people'

const call = async <T,>(method: 'GET' | 'POST' | 'PATCH' | 'PUT', url: string, data?: unknown): Promise<T> => {
  const response = await createApiCall({ method, url, data })
  if (response.status >= 400) throw new Error(response.data?.message || 'Request failed')
  return response.data as T
}

export const usePeople = () =>
  useQuery<Person[]>({
    queryKey: [PEOPLE_QUERY_KEY],
    queryFn: () => call<Person[]>('GET', '/internal/access/people'),
    staleTime: 1000 * 30,
  })

/** Every People write, each refreshing the table (and the legacy Signups queries). */
export const usePeopleActions = () => {
  const queryClient = useQueryClient()
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: [PEOPLE_QUERY_KEY] })
    queryClient.invalidateQueries({ queryKey: [SIGNUPS_QUERY_KEY] })
    queryClient.invalidateQueries({ queryKey: [SIGNUP_ALLOWLIST_QUERY_KEY] })
    queryClient.invalidateQueries({ queryKey: [SITE_GATE_INVITES_QUERY_KEY] })
  }
  // Called in a fixed order on every render, so the hook order is stable.
  const useAction = <V, R>(fn: (v: V) => Promise<R>) => useMutation<R, Error, V>({ mutationFn: fn, onSuccess: refresh })

  return {
    invite: useAction((v: InvitePersonPayload) => call<GrantResult>('POST', '/internal/access/invite', v)),
    grantWebsite: useAction((v: { key: string; name?: string; expires_at: string }) =>
      call<GrantResult>('POST', '/internal/access/website', v)),
    regenerateWebsite: useAction((v: { inviteId: string }) =>
      call<GrantResult>('POST', `/internal/access/website/${v.inviteId}/regenerate`)),
    updateWebsiteExpiry: useAction((v: { inviteId: string; expires_at: string }) =>
      call('PATCH', `/internal/access/website/${v.inviteId}/expiry`, { expires_at: v.expires_at })),
    revokeWebsite: useAction((v: { key: string }) => call('POST', '/internal/access/website/revoke', v)),
    grantPricing: useAction((v: { key: string; name?: string; send_email?: boolean }) =>
      call<GrantResult>('POST', '/internal/access/pricing', v)),
    revokePricing: useAction((v: { key: string }) => call('POST', '/internal/access/pricing/revoke', v)),
    grantPlatform: useAction((v: { key: string; name?: string; send_email?: boolean }) =>
      call<GrantResult>('POST', '/internal/access/platform', v)),
    revokePlatform: useAction((v: { key: string }) => call('POST', '/internal/access/platform/revoke', v)),
    platformLink: useAction((v: { key: string }) => call<{ platform_url: string }>('POST', '/internal/access/platform/link', v)),
    setNote: useAction((v: { key: string; note: string }) => call('PUT', '/internal/access/note', v)),
  }
}
